import type {
  GivingStatement,
  RelationshipLabel,
  CampaignCommitment,
  CampaignCommitmentPatch,
  Commitments,
  Address,
  AddressKind,
  ContactPatch,
  EmergencyContact,
  EmergencyContactInput,
  FinderGroup,
  Giving,
  Invoice,
  LocationGroup,
  MedicalData,
  MedicalItem,
  MedicalItemInput,
  MedicalProfile,
  MedicalProfilePatch,
  MyGroup,
  Privacy,
  ProfileMe,
  RelationshipRequest,
  RelationshipRequests,
  SearchHit,
  Subscriptions,
  SubscriptionsPatch,
  Commitment,
  FormerMember,
} from './types';
import { getMpWidgetToken } from './campus';
import { mpFetch } from '@church/nextjs-ui/website/mp-session';

// Vite bakes these at build time via `define` in vite.config.ts.
const BAKED_API_KEY = process.env.CHURCH_HUB_API_KEY ?? '';
const BAKED_APP_SLUG = process.env.CHURCH_HUB_APP_SLUG ?? '';

export function apiHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  if (BAKED_API_KEY) h['x-api-key'] = BAKED_API_KEY;
  if (BAKED_APP_SLUG) h['x-app-slug'] = BAKED_APP_SLUG;
  return h;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
  }
}

export const isUnauthorized = (err: unknown) => err instanceof ApiError && err.status === 401;

/**
 * One function per contract route. Everything but the two public reads
 * (campuses, the group catalogue) carries the MP widget token; a missing
 * token is a 401 before any request goes out, so the page can flip to its
 * signed-out state without a round trip.
 */
export interface ProfileApi {
  // reads
  getMe(signal?: AbortSignal): Promise<ProfileMe>;
  getLocations(signal?: AbortSignal): Promise<LocationGroup[]>;
  getRequests(signal?: AbortSignal): Promise<RelationshipRequests>;
  getMedical(contactId: number, signal?: AbortSignal): Promise<MedicalData>;
  getSubscriptions(contactId: number, signal?: AbortSignal): Promise<Subscriptions>;
  getGiving(year?: number, signal?: AbortSignal): Promise<Giving>;
  getGivingStatements(signal?: AbortSignal): Promise<GivingStatement[]>;
  getInvoices(signal?: AbortSignal): Promise<Invoice[]>;
  getCommitments(signal?: AbortSignal): Promise<Commitments>;
  getGroupCatalogue(signal?: AbortSignal): Promise<{ groups: FinderGroup[]; detailsUrl: string }>;
  getMyGroups(signal?: AbortSignal): Promise<MyGroup[]>;
  searchPeople(q: string, signal?: AbortSignal): Promise<SearchHit[]>;
  /** People who used to live in my household. */
  getFormerMembers(signal?: AbortSignal): Promise<FormerMember[]>;
  // writes
  /** Archive a Guest Child out of my household (nothing is deleted). */
  archiveMember(contactId: number): Promise<void>;
  /** Bring an archived former member back into my household. */
  bringBackMember(contactId: number): Promise<void>;
  updateContact(contactId: number, patch: ContactPatch): Promise<void>;
  updatePrivacy(contactId: number, patch: Partial<Privacy>): Promise<void>;
  uploadPhoto(contactId: number, file: File): Promise<{ imageUrl: string }>;
  removePhoto(contactId: number): Promise<void>;
  setEmergencyContact(
    contactId: number,
    input: EmergencyContactInput
  ): Promise<EmergencyContact | null>;
  /** `kind` picks the home (primary) or seasonal (alt) address; both live on the household. */
  updateAddress(address: Address, kind?: AddressKind): Promise<Address>;
  setHouseholdCampus(congregationId: number): Promise<void>;
  /** The header picker's own field (Contacts.Web_Congregation_ID) */
  setHomeCampus(congregationId: number): Promise<void>;
  sendRequest(input: {
    toContactId: number;
    relationshipId: number;
    note?: string;
  }): Promise<RelationshipRequest>;
  answerRequest(id: number, action: 'accept' | 'decline' | 'cancel'): Promise<void>;
  /** "relatedContactId is relationshipId to subjectContactId (me when omitted)"; answers the row as MP labels it */
  addRelationship(
    relatedContactId: number,
    relationshipId: number,
    subjectContactId?: number
  ): Promise<RelationshipLabel>;
  removeRelationship(contactRelationshipId: number): Promise<void>;
  /** Change what a relative is to the row's person; answers the new row as MP labels it. */
  changeRelationship(
    contactRelationshipId: number,
    relationshipId: number
  ): Promise<RelationshipLabel>;
  addMedicalItem(contactId: number, input: MedicalItemInput): Promise<MedicalItem>;
  updateMedicalItem(
    contactId: number,
    itemId: number,
    patch: Partial<MedicalItemInput>
  ): Promise<void>;
  removeMedicalItem(contactId: number, itemId: number): Promise<void>;
  updateMedicalProfile(contactId: number, patch: MedicalProfilePatch): Promise<MedicalProfile>;
  confirmMedical(contactId: number): Promise<MedicalProfile>;
  updateSubscriptions(contactId: number, patch: SubscriptionsPatch): Promise<Subscriptions>;
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  updateCampaignCommitment(
    commitmentId: number,
    patch: CampaignCommitmentPatch
  ): Promise<CampaignCommitment>;
  removeCampaignCommitment(commitmentId: number): Promise<void>;
}

async function readError(res: Response, fallback: string): Promise<ApiError> {
  let message = fallback;
  try {
    const data = (await res.json()) as { error?: string; message?: string };
    if (data.error) message = data.error;
    else if (data.message) message = data.message;
  } catch {
    /* no body */
  }
  return new ApiError(message, res.status);
}

export function createApi(apiBaseUrl: string): ProfileApi {
  const base = apiBaseUrl.replace(/\/$/, '');
  const url = (path: string) => `${base}/api${path}`;

  async function getPublic<T>(path: string, signal?: AbortSignal): Promise<T> {
    const res = await fetch(url(path), { headers: apiHeaders(), signal });
    if (!res.ok) throw await readError(res, `${path} responded ${res.status}`);
    return (await res.json()) as T;
  }

  function tokenOr401(): string {
    const token = getMpWidgetToken();
    if (!token) throw new ApiError('Sign in to see your profile', 401);
    return token;
  }

  async function getWithToken<T>(path: string, signal?: AbortSignal): Promise<T> {
    const token = tokenOr401();
    const res = await mpFetch(url(path), {
      headers: { ...apiHeaders(), Authorization: `Bearer ${token}` },
      signal,
    });
    if (!res.ok) throw await readError(res, `Could not load ${path}`);
    return (await res.json()) as T;
  }

  async function send<T>(
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    fallback = 'Could not save that'
  ): Promise<T> {
    const token = tokenOr401();
    const res = await mpFetch(url(path), {
      method,
      headers: {
        ...apiHeaders(),
        Authorization: `Bearer ${token}`,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw await readError(res, fallback);
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  return {
    getMe: (signal) => getWithToken<ProfileMe>('/profile/me', signal),
    getFormerMembers: async (signal) =>
      (await getWithToken<{ formerMembers: FormerMember[] }>('/profile/household/former', signal))
        .formerMembers ?? [],
    archiveMember: (contactId) =>
      send<void>('POST', `/profile/contacts/${contactId}/archive`, {}, 'Could not archive them'),
    bringBackMember: (contactId) =>
      send<void>('POST', `/profile/household/former/${contactId}`, {}, 'Could not bring them back'),
    getLocations: async (signal) => {
      const data = await getPublic<unknown>('/locations', signal);
      return Array.isArray(data) ? (data as LocationGroup[]) : [];
    },
    getRequests: (signal) =>
      getWithToken<RelationshipRequests>('/profile/relationship-requests', signal),
    getMedical: (contactId, signal) =>
      getWithToken<MedicalData>(`/profile/contacts/${contactId}/medical`, signal),
    getSubscriptions: (contactId, signal) =>
      getWithToken<Subscriptions>(`/profile/contacts/${contactId}/subscriptions`, signal),
    getGiving: (year, signal) =>
      getWithToken<Giving>(`/profile/giving${year ? `?year=${year}` : ''}`, signal),
    getGivingStatements: async (signal) =>
      (await getWithToken<{ statements: GivingStatement[] }>('/profile/giving/statements', signal))
        .statements,
    getInvoices: async (signal) =>
      (await getWithToken<{ invoices: Invoice[] }>('/profile/invoices', signal)).invoices,
    getCommitments: async (signal) => {
      const d = await getWithToken<{
        commitments: Commitment[];
        campaignCommitments?: CampaignCommitment[];
      }>('/profile/commitments', signal);
      return { pledges: d.commitments, campaign: d.campaignCommitments ?? [] };
    },
    updateCampaignCommitment: async (commitmentId, patch) =>
      (
        await send<{ commitment: CampaignCommitment }>(
          'PUT',
          `/profile/commitments/campaign/${commitmentId}`,
          patch,
          'Could not update your commitment'
        )
      ).commitment,
    removeCampaignCommitment: (commitmentId) =>
      send<void>(
        'DELETE',
        `/profile/commitments/campaign/${commitmentId}`,
        undefined,
        'Could not remove your commitment'
      ),
    getGroupCatalogue: async (signal) => {
      const data = await getPublic<{ groups: FinderGroup[]; links: { details: string } }>(
        '/groups',
        signal
      );
      return { groups: data.groups, detailsUrl: data.links.details };
    },
    getMyGroups: async (signal) =>
      (await getWithToken<{ groups: MyGroup[] }>('/groups/me', signal)).groups,
    searchPeople: async (q, signal) =>
      (
        await getWithToken<{ people: SearchHit[] }>(
          `/profile/people/search?q=${encodeURIComponent(q)}`,
          signal
        )
      ).people,

    updateContact: (contactId, patch) => send('PUT', `/profile/contacts/${contactId}`, patch),
    updatePrivacy: (contactId, patch) =>
      send('PUT', `/profile/contacts/${contactId}/privacy`, patch),
    uploadPhoto: async (contactId, file) => {
      const token = tokenOr401();
      const fd = new FormData();
      fd.append('file', file);
      const res = await mpFetch(url(`/profile/contacts/${contactId}/photo`), {
        method: 'POST',
        headers: { ...apiHeaders(), Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw await readError(res, 'Could not upload the photo');
      return (await res.json()) as { imageUrl: string };
    },
    removePhoto: (contactId) =>
      send(
        'DELETE',
        `/profile/contacts/${contactId}/photo`,
        undefined,
        'Could not remove the photo'
      ),
    setEmergencyContact: async (contactId, input) =>
      (
        await send<{ ok: boolean; emergencyContact: EmergencyContact | null }>(
          'PUT',
          `/profile/contacts/${contactId}/emergency-contact`,
          input
        )
      ).emergencyContact,
    updateAddress: async (address, kind = 'primary') =>
      (
        await send<{ ok: boolean; address: Address }>('PUT', '/profile/household/address', {
          ...address,
          kind,
        })
      ).address,
    setHouseholdCampus: (congregationId) =>
      send('PUT', '/profile/household/campus', { congregationId }),
    setHomeCampus: (congregationId) => send('POST', '/set-home-campus', { congregationId }),
    sendRequest: async (input) =>
      (
        await send<{ request: RelationshipRequest }>(
          'POST',
          '/profile/relationship-requests',
          input,
          'Could not send the request'
        )
      ).request,
    answerRequest: (id, action) => send('PUT', `/profile/relationship-requests/${id}`, { action }),
    addRelationship: async (relatedContactId, relationshipId, subjectContactId) =>
      (
        await send<{ ok: true; relationship: RelationshipLabel }>(
          'POST',
          '/profile/relationships',
          {
            relatedContactId,
            relationshipId,
            ...(subjectContactId ? { subjectContactId } : {}),
          }
        )
      ).relationship,
    removeRelationship: (contactRelationshipId) =>
      send('DELETE', `/profile/relationships/${contactRelationshipId}`),
    changeRelationship: async (contactRelationshipId, relationshipId) =>
      (
        await send<{ ok: true; relationship: RelationshipLabel }>(
          'PATCH',
          `/profile/relationships/${contactRelationshipId}`,
          { relationshipId }
        )
      ).relationship,
    addMedicalItem: async (contactId, input) =>
      (
        await send<{ item: MedicalItem }>(
          'POST',
          `/profile/contacts/${contactId}/medical/items`,
          input
        )
      ).item,
    updateMedicalItem: (contactId, itemId, patch) =>
      send('PUT', `/profile/contacts/${contactId}/medical/items/${itemId}`, patch),
    removeMedicalItem: (contactId, itemId) =>
      send('DELETE', `/profile/contacts/${contactId}/medical/items/${itemId}`),
    updateMedicalProfile: async (contactId, patch) =>
      (
        await send<{ profile: MedicalProfile }>(
          'PUT',
          `/profile/contacts/${contactId}/medical/profile`,
          patch
        )
      ).profile,
    confirmMedical: async (contactId) =>
      (
        await send<{ profile: MedicalProfile }>(
          'POST',
          `/profile/contacts/${contactId}/medical/confirm`
        )
      ).profile,
    updateSubscriptions: (contactId, patch) =>
      send<Subscriptions>('PATCH', `/profile/contacts/${contactId}/subscriptions`, patch),
    changePassword: (currentPassword, newPassword) =>
      send(
        'PUT',
        '/profile/password',
        { currentPassword, newPassword },
        'Could not change your password'
      ),
  };
}
